<template>
	<div class="head-image" @click="showUserInfo($event)" :style="[avatarFrameStyle, { cursor: isShowUserInfo ? 'pointer' : null }]">
		<asset-image
			class="avatar-image"
			v-show="url"
			:class="{ 'avatar-image--ready': imageAvailable }"
			:src="url"
			:strict="strict"
			:keep-previous="true"
			:style="avatarImageStyle"
			loading="lazy"
			@load="onAssetImageLoad"
			@resolve-error="onAssetImageFail"
			@error="onAssetImageFail"
		/>
			<div class="avatar-text" :class="{ 'avatar-text--group': isGroupVariant }"
				v-show="!url || !imageAvailable" :style="avatarTextStyle">
				<i v-if="isGroupVariant" class="avatar-group-icon icon iconfont icon-group"></i>
				<span v-else>{{ avatarText }}</span>
			</div>
		<div v-show="online" class="online" title="用户当前在线"></div>
		<slot></slot>
	</div>
</template>

<script>
import AssetImage from './AssetImage.vue';
import { getAvatarColorIndex, getAvatarPlaceholderText, normalizeAvatarName } from '../../utils/avatarPlaceholder.js';

const AVATAR_PALETTE_LENGTH = 6;

export default {
	name: "headImage",
	components: {
		AssetImage
	},
	data() {
		return {
			imageAvailable: false
		}
	},
	props: {
		id: {
			type: Number
		},
		size: {
			type: Number,
			default: 42
		},
		width: {
			type: Number
		},
		height: {
			type: Number
		},
		radius: {
			type: String,
			default: "50%"
		},
		url: {
			type: String
		},
		name: {
			type: String,
			default: null
		},
		online: {
			type: Boolean,
			default: false
		},
		isShowUserInfo: {
			type: Boolean,
			default: true
		},
		strict: {
			type: Boolean,
			default: true
		},
		variant: {
			type: String,
			default: 'user',
			validator(value) {
				return ['user', 'group'].includes(value);
			}
		}
	},
	watch: {
		url: {
			immediate: true,
			handler(url) {
				if (!url) {
					this.imageAvailable = false;
				}
			}
		}
	},
	methods: {
		showUserInfo(e) {
			if (!this.isShowUserInfo) return;
			if (this.id && this.id > 0) {
				this.$http({
					url: `/user/find/${this.id}`,
					method: 'get'
				}).then((user) => {
					let pos = {
						x: e.x + 30,
						y: e.y
					}
					this.$eventBus.$emit("openUserInfo", user, pos);
				})
			}
		},
		onAssetImageLoad() {
			this.imageAvailable = true;
			this.$emit('load', this.url);
		},
		onAssetImageFail() {
			this.imageAvailable = false;
			this.$emit('error', this.url);
		}

	},
	computed: {
		isGroupVariant() {
			return this.variant === 'group';
		},
		normalizedName() {
			return normalizeAvatarName(this.name);
		},
		avatarFrameStyle() {
			return {
				width: `${this.avatarWidth}px`,
				height: `${this.avatarHeight}px`,
				borderRadius: this.radius,
				boxSizing: 'border-box'
			};
		},
		avatarImageStyle() {
			return {
				width: `${this.avatarWidth}px`,
				height: `${this.avatarHeight}px`,
				borderRadius: this.radius
			};
		},
		avatarTextStyle() {
			const avatarSize = Math.min(this.avatarWidth, this.avatarHeight);
			return {
				width: `${this.avatarWidth}px`,
				height: `${this.avatarHeight}px`,
				background: this.avatarBackground,
				fontSize: `${Math.max(11, Math.round(avatarSize * 0.42))}px`,
				borderRadius: this.radius
			};
		},
		avatarWidth() {
			return this.width ? this.width : this.size;
		},
		avatarHeight() {
			return this.height ? this.height : this.size;
		},
		avatarText() {
			return getAvatarPlaceholderText(this.normalizedName);
		},
		avatarBackground() {
			const paletteIndex = getAvatarColorIndex(this.avatarText, AVATAR_PALETTE_LENGTH) + 1;
			const fallback = 'var(--im-avatar-bg-default, var(--im-color-primary))';
			const color = `var(--im-avatar-bg-${paletteIndex}, ${fallback})`;
			return `radial-gradient(circle at 30% 24%, rgba(var(--im-white-rgb), 0.18), transparent 52%), linear-gradient(145deg, rgba(var(--im-white-rgb), 0.08), rgba(var(--im-black-rgb), 0.12)), ${color}`;
		}
	}
}
</script>

<style scoped lang="scss">
.head-image {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	line-height: 1;
	vertical-align: top;

	.avatar-image {
		position: absolute;
		inset: 0;
		overflow: hidden;
		display: block;
		object-fit: cover;
		opacity: 0;
		transition: opacity 0.16s ease;
	}

	.avatar-image--ready {
		opacity: 1;
	}

	.avatar-text {
		position: absolute;
		inset: 0;
		box-sizing: border-box;
		color: var(--im-avatar-text, var(--im-text-color-white));
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 600;
		line-height: 1;
		box-shadow: inset 0 0 0 1px var(--im-avatar-border, rgba(var(--im-black-rgb), 0.06));
		user-select: none;
		overflow: hidden;

		&--group {
			box-shadow: inset 0 0 0 1px rgba(var(--im-white-rgb), 0.22),
				inset 0 0 0 2px var(--im-avatar-border, rgba(var(--im-black-rgb), 0.06));
		}

		.avatar-group-icon {
			font-size: 1em;
			line-height: 1;
			max-width: 82%;
			max-height: 82%;
		}
	}

	.online {
		position: absolute;
		z-index: 1;
		right: -1px;
		bottom: 1px;
		width: 20%;
		height: 20%;
		min-width: 9px;
		min-height: 9px;
		background: var(--im-color-success);
		border-radius: 50%;
		border: 2px solid var(--im-session-bg, var(--im-background));
	}
}
</style>
